import { useNavigate } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';
import { api } from '@services';
import axios from 'axios';
import { validateMetricValue } from '@utils';
import { API_CONFIG } from '../../../config/api';

// Import types
import {
  HotelInfo,
  Metric,
  ExistingHotel,
  AiInsights,
  OCRFinancialData,
  DealData
} from '@features/deals/types';

// Import constants
import { DEFAULT_HOTEL_INFO, CURRENT_YEAR } from '@features/deals/components/constants';
import { useState, useEffect, useCallback } from 'react';

export const useDealWizard = () => {
  // Navigation
  const navigate = useNavigate();

  // State
  const [activeStep, setActiveStep] = useState<number>(0);
  const [hotelInfo, setHotelInfo] = useState<HotelInfo>(DEFAULT_HOTEL_INFO);

  const [existingHotels, setExistingHotels] = useState<ExistingHotel[]>([]);
  const [selectedHotelId, setSelectedHotelId] = useState<string>('');
  const [isNewHotel, setIsNewHotel] = useState<boolean>(true);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [aiInsights, setAiInsights] = useState<AiInsights | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [dealData, setDealData] = useState<DealData>({
    name: '',
    asking_price: '',
    hotelInfo,
    metrics,
  });
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [newItemName, setNewItemName] = useState<string>('');
  const [newItemYear, setNewItemYear] = useState<string>(CURRENT_YEAR.toString());
  const [newItemAmount, setNewItemAmount] = useState<string>('');
  const [success, setSuccess] = useState<string | null>(null);
  const [ocrFinancialData, setOcrFinancialData] = useState<OCRFinancialData | null>(null);
  const [hotelAiAnalysis, setHotelAiAnalysis] = useState<any>(null);
  const [showLoginDialog, setShowLoginDialog] = useState<boolean>(false);
  const [editedData, setEditedData] = useState<any>({});
  const [deletedRows, setDeletedRows] = useState<Set<string>>(new Set());

  // Effects
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await api.get('/hotels');
        setExistingHotels(response.data);
      } catch (error) {
        console.error('Error fetching hotels:', error);
        setError('Failed to load existing hotels');
      }
    };
    fetchHotels();
  }, []);

  useEffect(() => {
    setDealData((prev) => ({
      ...prev,
      hotelInfo,
      metrics,
    }));
  }, [hotelInfo, metrics]);

  useEffect(() => {
    const fetchOCRFinancials = async () => {
      if (!selectedHotelId) return;

      try {
        // Fetch all required hotel financial data in parallel
        const [
          ocrResponse,
          hotelMetricsResponse,
          occupancyResponse,
          adrResponse,
          revparResponse,
          profitLossResponse
        ] = await Promise.all([
          api.get(`/ocr/hotel/${selectedHotelId}/financials`),
          api.get(`/hotels/${selectedHotelId}/metrics`).catch(() => ({ data: [] })),
          api.get(`/hotels/${selectedHotelId}/occupancy_metrics`).catch(() => ({ data: [] })),
          api.get(`/hotels/${selectedHotelId}/adr_metrics`).catch(() => ({ data: [] })),
          api.get(`/hotels/${selectedHotelId}/revpar_metrics`).catch(() => ({ data: [] })),
          api.get(`/hotels/${selectedHotelId}/profit-loss`).catch(() => ({ data: [] }))
        ]);

        console.log('Fetched hotel financial data:', {
          ocr: ocrResponse.data ? 'OCR data available' : 'No OCR data',
          metrics: hotelMetricsResponse.data?.length || 0,
          occupancy: occupancyResponse.data?.length || 0,
          adr: adrResponse.data?.length || 0,
          revpar: revparResponse.data?.length || 0,
          profitLoss: profitLossResponse.data?.length || 0
        });

        setOcrFinancialData(ocrResponse.data);

        const convertedMetrics: Metric[] = [];

        // Convert OCR data to metrics format
        if (ocrResponse.data) {
          interface OCRItem {
            item_name: string;
            values: { [key: string]: number };
          }

          interface OCRCategory {
            id: number;
            name: string;
          }

          Object.entries(ocrResponse.data.data).forEach(([categoryId, items]) => {
            const category = ocrResponse.data.categories.find((c: OCRCategory) => c.id === Number(categoryId));
            if (category) {
              (items as OCRItem[]).forEach((item: OCRItem) => {
                Object.entries(item.values).forEach(([year, value]) => {
                  if (value !== null && !isNaN(Number(value))) {
                    convertedMetrics.push({
                      category: category.name,
                      item: item.item_name,
                      value: value.toString(),
                      year: parseInt(year),
                      source: 'ocr',
                    });
                  }
                });
              });
            }
          });
        }

        // Process standard hotel metrics (KPIs)
        if (hotelMetricsResponse.data && Array.isArray(hotelMetricsResponse.data)) {
          hotelMetricsResponse.data.forEach((metric: any) => {
            if (metric.value !== null && !isNaN(Number(metric.value))) {
              const metricType = metric.type || '';
              let category = metricType;

              // Map types to standardized categories
              if (metricType.toLowerCase().includes('occupancy')) {
                category = 'Occupancy Rate';
              } else if (metricType.toLowerCase().includes('adr')) {
                category = 'ADR';
              } else if (metricType.toLowerCase().includes('revpar')) {
                category = 'RevPAR';
              }

              convertedMetrics.push({
                category,
                item: category,
                value: metric.value.toString(),
                year: parseInt(metric.year),
                source: 'confirmed',
                readOnly: true,
              });
            }
          });
        }

        // Process occupancy metrics
        if (occupancyResponse.data && Array.isArray(occupancyResponse.data)) {
          occupancyResponse.data.forEach((metric: any) => {
            if (metric.value !== null && !isNaN(Number(metric.value))) {
              convertedMetrics.push({
                category: 'Occupancy Rate',
                item: metric.period || 'Annual',
                value: metric.value.toString(),
                year: parseInt(metric.year),
                source: 'confirmed',
                readOnly: true,
              });
            }
          });
        }

        // Process ADR metrics
        if (adrResponse.data && Array.isArray(adrResponse.data)) {
          adrResponse.data.forEach((metric: any) => {
            if (metric.value !== null && !isNaN(Number(metric.value))) {
              convertedMetrics.push({
                category: 'ADR',
                item: metric.period || 'Annual',
                value: metric.value.toString(),
                year: parseInt(metric.year),
                source: 'confirmed',
                readOnly: true,
              });
            }
          });
        }

        // Process RevPAR metrics
        if (revparResponse.data && Array.isArray(revparResponse.data)) {
          revparResponse.data.forEach((metric: any) => {
            if (metric.value !== null && !isNaN(Number(metric.value))) {
              convertedMetrics.push({
                category: 'RevPAR',
                item: metric.period || 'Annual',
                value: metric.value.toString(),
                year: parseInt(metric.year),
                source: 'confirmed',
                readOnly: true,
              });
            }
          });
        }

        // Process profit/loss metrics
        if (profitLossResponse.data && Array.isArray(profitLossResponse.data)) {
          profitLossResponse.data.forEach((item: any) => {
            if (item.value !== null && !isNaN(Number(item.value))) {
              convertedMetrics.push({
                category: item.category,
                item: item.item || '',
                value: item.value.toString(),
                year: parseInt(item.year),
                source: 'confirmed',
                readOnly: true,
              });
            }
          });
        }

        // Add these metrics to the existing ones, avoiding duplicates
        setMetrics(prevMetrics => {
          // Filter out any metrics with the same category, item, and year
          const filteredMetrics = prevMetrics.filter(existing =>
            !convertedMetrics.some(newMetric =>
              existing.category === newMetric.category &&
              existing.year === newMetric.year &&
              existing.item === newMetric.item
            )
          );

          const combinedMetrics = [...filteredMetrics, ...convertedMetrics];
          console.log(`Combined ${convertedMetrics.length} new metrics with ${filteredMetrics.length} existing metrics`);
          return combinedMetrics;
        });
      } catch (error: any) {
        console.error("Error loading financial data:", error);
        setError('Failed to load hotel financial data');
      }
    };

    fetchOCRFinancials();
  }, [selectedHotelId]);

  // Handlers
  const handleHotelSelect = useCallback(
    async (event: SelectChangeEvent<string>) => {
      const value = event.target.value;
      if (value === 'new') {
        setIsNewHotel(true);
        setSelectedHotelId('');
        setHotelInfo(DEFAULT_HOTEL_INFO);
        setMetrics([]);
        setHotelAiAnalysis(null); // Reset AI analysis
      } else {
        setIsNewHotel(false);
        setSelectedHotelId(value);
        setLoading(true);
        const selectedHotel = existingHotels.find((h) => h.id === value);

        if (selectedHotel) {
          try {
            // Fetch hotel's detailed financial data in parallel
            const [
              financialsResponse,
              aiAnalysisResponse,
              profitLossResponse,
              hotelResponse,
              occupancyResponse,
              adrResponse,
              revparResponse
            ] = await Promise.all([
              api.get(`/hotels/${selectedHotel.id}/confirmed-financials`),
              api.get(`/hotels/${selectedHotel.id}/ai-analysis`).catch(() => ({ data: null })),
              api.get(`/hotels/${selectedHotel.id}/profit-loss`).catch(() => ({ data: [] })),
              api.get(`/hotels/${selectedHotel.id}`).catch(() => ({ data: null })),
              api.get(`/hotels/${selectedHotel.id}/occupancy_metrics`).catch(() => ({ data: [] })),
              api.get(`/hotels/${selectedHotel.id}/adr_metrics`).catch(() => ({ data: [] })),
              api.get(`/hotels/${selectedHotel.id}/revpar_metrics`).catch(() => ({ data: [] }))
            ]);

            console.log('Hotel selection - fetched data:', {
              financials: financialsResponse.data?.length || 0,
              aiAnalysis: !!aiAnalysisResponse.data,
              profitLoss: profitLossResponse.data?.length || 0,
              hotel: !!hotelResponse.data,
              occupancy: occupancyResponse.data?.length || 0,
              adr: adrResponse.data?.length || 0,
              revpar: revparResponse.data?.length || 0
            });

            // Process confirmed financials
            const confirmedFinancials = financialsResponse.data.map((row: any) => ({
              category: row.category,
              item: '',
              value: row.value.toString(),
              year: row.year,
              source: 'confirmed' as const,
              readOnly: true,
            }));

            // Collect all metrics here
            let allMetrics: Metric[] = [];

            // Process profit/loss metrics for more detailed financial data
            if (profitLossResponse.data && Array.isArray(profitLossResponse.data)) {
              const profitLossMetrics = profitLossResponse.data.map((item: any) => ({
                category: item.category,
                item: item.item || '',
                value: item.value.toString(),
                year: parseInt(item.year),
                source: 'confirmed' as const,
                readOnly: true,
              }));

              allMetrics = [...allMetrics, ...profitLossMetrics];
            }

            // Process occupancy metrics
            if (occupancyResponse.data && Array.isArray(occupancyResponse.data)) {
              const occupancyMetrics = occupancyResponse.data.map((item: any) => ({
                category: 'Occupancy Rate',
                item: item.period || 'Annual',
                value: item.value.toString(),
                year: parseInt(item.year),
                source: 'confirmed' as const,
                readOnly: true,
              }));

              allMetrics = [...allMetrics, ...occupancyMetrics];
            }

            // Process ADR metrics
            if (adrResponse.data && Array.isArray(adrResponse.data)) {
              const adrMetrics = adrResponse.data.map((item: any) => ({
                category: 'ADR',
                item: item.period || 'Annual',
                value: item.value.toString(),
                year: parseInt(item.year),
                source: 'confirmed' as const,
                readOnly: true,
              }));

              allMetrics = [...allMetrics, ...adrMetrics];
            }

            // Process RevPAR metrics
            if (revparResponse.data && Array.isArray(revparResponse.data)) {
              const revparMetrics = revparResponse.data.map((item: any) => ({
                category: 'RevPAR',
                item: item.period || 'Annual',
                value: item.value.toString(),
                year: parseInt(item.year),
                source: 'confirmed' as const,
                readOnly: true,
              }));

              allMetrics = [...allMetrics, ...revparMetrics];
            }

            // If we still need more metrics, try to extract from hotel financials
            if (allMetrics.length === 0 && hotelResponse.data?.financials && Array.isArray(hotelResponse.data.financials)) {
              // If no specific P&L data, try to generate from hotel financials
              const hotelMetrics = hotelResponse.data.financials.flatMap((record: any) => {
                const metrics: Metric[] = [];

                // Add revenue
                if (record.revenue) {
                  metrics.push({
                    category: 'Revenue',
                    item: 'Room Revenue',
                    value: record.revenue.toString(),
                    year: record.year,
                    source: 'confirmed' as const,
                  });
                }

                // Add expenses
                if (record.expenses) {
                  metrics.push({
                    category: 'Expenses',
                    item: 'Total Expenses',
                    value: record.expenses.toString(),
                    year: record.year,
                    source: 'confirmed' as const,
                  });
                }

                // Add NOI
                if (record.noi) {
                  metrics.push({
                    category: 'Net Operating Income',
                    item: 'NOI',
                    value: record.noi.toString(),
                    year: record.year,
                    source: 'confirmed' as const,
                  });
                }

                // Add KPI metrics
                if (record.occupancyRate) {
                  metrics.push({
                    category: 'Occupancy Rate',
                    item: 'Occupancy Rate',
                    value: record.occupancyRate.toString(),
                    year: record.year,
                    source: 'confirmed' as const,
                  });
                }

                if (record.adr) {
                  metrics.push({
                    category: 'ADR',
                    item: 'ADR',
                    value: record.adr.toString(),
                    year: record.year,
                    source: 'confirmed' as const,
                  });
                }

                if (record.revPAR) {
                  metrics.push({
                    category: 'RevPAR',
                    item: 'RevPAR',
                    value: record.revPAR.toString(),
                    year: record.year,
                    source: 'confirmed' as const,
                  });
                }

                return metrics;
              });

              allMetrics = [...allMetrics, ...hotelMetrics];
            }

            // Add confirmed financials that don't already exist in the metrics
            allMetrics = [
              ...allMetrics,
              ...confirmedFinancials.filter((cf: Metric) =>
                !allMetrics.some(m =>
                  m.category === cf.category && m.year === cf.year
                )
              )
            ];

            // Set hotel info
            setHotelInfo({
              ...selectedHotel,
              amenities: selectedHotel.amenities || [],
            });

            // Store hotel AI analysis if available
            if (aiAnalysisResponse.data) {
              setHotelAiAnalysis(aiAnalysisResponse.data);
            } else {
              setHotelAiAnalysis(null);
            }

            // Keep only manual metrics and add all confirmed ones
            const manualMetrics = metrics.filter((m) => m.source === 'manual');
            const combinedMetrics = [...allMetrics, ...manualMetrics];

            console.log(`Hotel select: Combined ${allMetrics.length} metrics from hotel with ${manualMetrics.length} manual metrics`);
            setMetrics(combinedMetrics);

          } catch (error) {
            console.error('Error fetching hotel financial data:', error);
            setError('Failed to load hotel financial data');
            // Still update the hotel info but with a notice about the error
            setHotelInfo({
              ...selectedHotel,
              amenities: selectedHotel.amenities || [],
            });
          } finally {
            setLoading(false);
          }
        }
      }
    },
    [existingHotels, metrics]
  );

  const handleAddNewItem = async () => {
    if (!selectedCategory || !newItemName.trim() || !newItemYear || !newItemAmount) {
      setError('Please fill in all fields for the new item');
      return;
    }

    try {
      const numericValue = parseFloat(newItemAmount);
      if (isNaN(numericValue)) {
        setError('Amount must be a valid number');
        return;
      }

      // Find category ID from OCR data if available
      let categoryId: number | null | undefined = null;
      if (ocrFinancialData?.categories) {
        const category = ocrFinancialData.categories.find((cat) => cat.name === selectedCategory || selectedCategory.includes(cat.name));
        categoryId = category?.id;
      }

      // If we have a selected hotel and category ID, save to backend
      if (selectedHotelId && categoryId) {
        const valuesObject = {
          [newItemYear]: numericValue,
        };

        const newData = {
          category_id: categoryId,
          item_name: newItemName.trim(),
          values: JSON.stringify(valuesObject),
          file_id: crypto.randomUUID(),
        };

        await api.post(`/ocr/hotel/${selectedHotelId}/add-item`, newData);
      }

      // Add to local metrics state
      const updatedMetric: Metric = {
        category: selectedCategory,
        item: newItemName.trim(),
        value: numericValue,
        year: parseInt(newItemYear),
        source: 'manual',
      };

      setMetrics((prevMetrics) => [...prevMetrics, updatedMetric]);

      // Reset form
      setNewItemName('');
      setNewItemYear(CURRENT_YEAR.toString());
      setNewItemAmount('');
      setSelectedCategory('');
      setSuccess('Item added successfully');
      setError(null);
    } catch (error: any) {
      setError(error.response?.data?.details || 'Failed to add new item');
    }
  };

  // Auth verification
  const verifyAuthAndContinue = async () => {
    try {
      setLoading(true);

      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required - please log in');
        setShowLoginDialog(true);
        setLoading(false);
        return;
      }

      console.log('Verifying authentication with token:', token.substring(0, 15) + '...');

      // Try a simple authenticated request to verify token works
      const testUrl = `${API_CONFIG.BASE_URL}/api/deals`;
      try {
        await axios.get(testUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          withCredentials: true
        });
        console.log('✅ Authentication verified successfully');
      } catch (authError: unknown) {
        if (axios.isAxiosError(authError) && authError.response?.status === 401) {
          // If test fails with 401, token is invalid
          console.error('❌ Authentication test failed with 401:', authError);
          throw authError;
        } else {
          // Other errors (404, 500, etc.) don't necessarily mean the token is invalid
          // so we can continue with analysis
          if (axios.isAxiosError(authError)) {
            console.log('Authentication test had non-401 error:', authError.response?.status);
          } else {
            console.log('Authentication test had unknown error');
          }
        }
      }

      // Now proceed with the analysis
      await performAnalysis();

    } catch (error) {
      console.error('Error in auth verification or analysis flow:', error);
      // If we get 401, show login dialog
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        setError('Authentication required - please log in');
        setShowLoginDialog(true);
      } else {
        setError('Failed to process analysis: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Analysis logic
  const performAnalysis = async () => {
    try {
      // Format metrics for analysis
      const cleanedMetrics = metrics.map((m) => ({
        category: m.category.trim(),
        item: m.item || '', // Include the required item property
        value: typeof m.value === 'string' ? Number(m.value.replace(/[^0-9.-]/g, '')) : m.value,
        year: m.year,
      }));

      const formattedHotelInfo = {
        ...hotelInfo,
        star_rating: Number(hotelInfo.star_rating),
        num_rooms: Number(hotelInfo.num_rooms),
        year_built: Number(hotelInfo.year_built),
        lot_size: Number(hotelInfo.lot_size),
        amenities: Array.isArray(hotelInfo.amenities) ? hotelInfo.amenities : [],
      };

      // Make the analysis call
      const result = await api.analyze(formattedHotelInfo, cleanedMetrics, hotelAiAnalysis);

      if (!result.insights || typeof result.insights !== 'object') {
        throw new Error('Invalid analysis response format');
      }

      // Adjust valuation estimate using hotel AI analysis if available
      if (hotelAiAnalysis && hotelAiAnalysis.valuationInsights?.estimatedValue) {
        // Adjust valuation estimate to consider hotel AI analysis
        const aiLow = hotelAiAnalysis.valuationInsights.estimatedValue.low;
        const aiHigh = hotelAiAnalysis.valuationInsights.estimatedValue.high;

        // Calculate weighted average
        result.insights.valuationEstimate.low = Math.round(result.insights.valuationEstimate.low * 0.7 + aiLow * 0.3);
        result.insights.valuationEstimate.high = Math.round(result.insights.valuationEstimate.high * 0.7 + aiHigh * 0.3);
      }

      setAiInsights(result.insights);
      setActiveStep((prev) => prev + 1);
    } catch (error) {
      console.error('Analysis execution error:', error);
      throw error;
    }
  };

  const handleLoginSuccess = () => {
    setSuccess('Login successful! You can now continue.');
    setError(null);

    // If we were trying to analyze (step 2), add a short delay to ensure
    // the token is properly saved in localStorage before retrying
    if (activeStep === 2) {
      // Add loading state first
      setLoading(true);

      // Check token first to make sure it's actually saved
      const token = localStorage.getItem('token');
      console.log('Token available after login:', !!token, token ? `(length: ${token.length})` : '');

      // Add a small delay to ensure token is fully saved before proceeding
      setTimeout(() => {
        verifyAuthAndContinue(); // Use the new verification flow
      }, 1000); // Increased delay to ensure token is fully saved
    }
  };

  const handleNext = useCallback(async () => {
    setError(null);

    if (activeStep === 1) {
      try {
        await handleSaveFinancials();
        return;
      } catch (error) {
        return;
      }
    }

    if (activeStep === 2) {
      try {
        // Validate hotel info
        const requiredFields = {
          name: hotelInfo.name?.trim(),
          address: hotelInfo.address?.trim(),
          city: hotelInfo.city?.trim(),
          state: hotelInfo.state?.trim(),
          country: hotelInfo.country?.trim(),
          star_rating: hotelInfo.star_rating,
          num_rooms: hotelInfo.num_rooms,
        };

        const invalidFields = Object.entries(requiredFields)
          .filter(([_, value]) => {
            if (typeof value === 'string') return !value;
            return value <= 0;
          })
          .map(([key]) => key.replace(/_/g, ' '));

        if (invalidFields.length > 0) {
          throw new Error(`Missing required fields: ${invalidFields.join(', ')}`);
        }

        if (!metrics.length) {
          throw new Error('Please add at least one metric');
        }

        // Run the auth verification and analysis flow
        await verifyAuthAndContinue();

      } catch (err: any) {
        console.error('Error in analysis preparation:', err);
        setError(err?.response?.data?.details || err.message || 'Analysis failed');
        setLoading(false);
      }
      return;
    }

    setActiveStep((prev) => prev + 1);
  }, [activeStep, hotelInfo, metrics, hotelAiAnalysis]);

  const handleBack = () => {
    setError(null);
    if (activeStep === 3) setAiInsights(null);
    if (activeStep === 4) setSaveStatus('idle');
    setActiveStep((prev) => prev - 1);
  };

  const handleBackClick = () => {
    if (activeStep === 3 || (activeStep === 4 && saveStatus !== 'success')) {
      setShowBackConfirm(true);
    } else {
      handleBack();
    }
  };

  const handleBackConfirm = (confirmed: boolean) => {
    setShowBackConfirm(false);
    if (confirmed) handleBack();
  };

  const handleSaveDeal = async () => {
    setLoading(true);
    setSaveStatus('idle');

    try {
      // Validate required fields
      if (!dealData.name.trim()) {
        throw new Error('Deal name is required');
      }
      if (!dealData.asking_price || isNaN(parseFloat(dealData.asking_price))) {
        throw new Error('Valid asking price is required');
      }
      if (!metrics || metrics.length === 0) {
        throw new Error('At least one financial metric is required');
      }
      if (!aiInsights) {
        throw new Error('AI analysis is required');
      }

      // Format metrics to ensure valid numbers and required fields
      const formattedMetrics = metrics
        .map((m) => ({
          category: m.category.trim(),
          value: typeof m.value === 'string' ? Number(m.value.replace(/[^0-9.-]/g, '')) : Number(m.value),
          year: m.year || new Date().getFullYear(),
        }))
        .filter((m) => !isNaN(m.value)); // Filter out any invalid numbers

      // Create the payload
      const payload = {
        name: dealData.name.trim(),
        asking_price: parseFloat(dealData.asking_price),
        hotelInfo: {
          ...hotelInfo,
          id: isNewHotel ? undefined : selectedHotelId,
          star_rating: Number(hotelInfo.star_rating),
          num_rooms: Number(hotelInfo.num_rooms),
          year_built: Number(hotelInfo.year_built),
          lot_size: Number(hotelInfo.lot_size),
          amenities: JSON.stringify(hotelInfo.amenities || []),
        },
        metrics: formattedMetrics,
        insights: aiInsights,
        hotelAiAnalysis: hotelAiAnalysis, // Include hotel AI analysis data in the payload
      };

      const response = await api.post('/deals', payload);
      setSaveStatus('success');
      setTimeout(() => navigate(`/deals/${response.data.dealId}`), 1500);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error
        || err?.response?.data?.message
        || err.message
        || 'Failed to save deal';
      setError(errorMessage);
      setSaveStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFinancials = async () => {
    if (!selectedHotelId) {
      setActiveStep((prevStep) => prevStep + 1);
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      // Generate or use existing fileId
      const fileId = ocrFinancialData?.data ? Object.values(ocrFinancialData.data).find((items) => items[0]?.file_id)?.[0]?.file_id || crypto.randomUUID() : crypto.randomUUID();

      // Convert metrics to category-based structure
      const categoryData: Record<
        string,
        Array<{
          item_name: string;
          values: string; // JSON stringified object
          file_id: string;
        }>
      > = {};

      // Track deleted items with proper typing
      const deletedItems: Array<{
        category_id: number;
        item_name: string;
      }> = [];

      // Process deleted items first - using the new deletion identifier format
      if (ocrFinancialData?.categories) {
        Array.from(deletedRows).forEach((key) => {
          // Parse the deletion key format: "category-item-year"
          const parts = key.split('-');
          if (parts.length >= 2) {
            const category = parts[0];
            const item = parts.slice(1, parts.length - 1).join('-'); // Handle multi-part item names
            const year = parts[parts.length - 1];

            // Find the category ID
            const categoryObj = ocrFinancialData.categories.find((cat) => cat.name === category);
            if (categoryObj) {
              deletedItems.push({
                category_id: categoryObj.id,
                item_name: item || category,
              });
            }
          }
        });
      }

      // Process metrics that are not deleted
      metrics.forEach((metric) => {
        // Skip already confirmed metrics - we don't need to save these again
        if (metric.source === 'confirmed') return;

        // Check if this metric is not in the deleted rows
        const uniqueId = `${metric.category}-${metric.item || ''}-${metric.year}`;
        if (!deletedRows.has(uniqueId)) {
          // Try to find the category ID - first from OCR data if available
          let categoryId: string | undefined;

          if (ocrFinancialData?.categories) {
            // Try exact match first
            const exactCategory = ocrFinancialData.categories.find(cat => cat.name === metric.category);
            if (exactCategory) {
              categoryId = exactCategory.id.toString();
            } else {
              // Try partial match as fallback
              const partialMatch = ocrFinancialData.categories.find(cat =>
                metric.category.includes(cat.name) || cat.name.includes(metric.category)
              );
              if (partialMatch) {
                categoryId = partialMatch.id.toString();
              }
            }
          }

          // If no category ID found, create a placeholder using the category name to avoid data loss
          if (!categoryId) {
            // Create a simple hash of the category name as ID
            const hashStr = metric.category
              .split('')
              .map(char => char.charCodeAt(0))
              .reduce((hash, code) => hash + code, 0)
              .toString();
            categoryId = `new_${hashStr}`;
          }

          if (!categoryData[categoryId]) {
            categoryData[categoryId] = [];
          }

          const numericValue = typeof metric.value === 'string'
            ? Number(metric.value.replace(/[^0-9.-]/g, ''))
            : metric.value;

          const existingItem = categoryData[categoryId].find((item) =>
            item.item_name === (metric.item || metric.category)
          );

          if (existingItem) {
            // Update existing item values
            const values = JSON.parse(existingItem.values);
            values[metric.year] = numericValue;
            existingItem.values = JSON.stringify(values);
          } else {
            // Add new item
            categoryData[categoryId].push({
              item_name: metric.item || metric.category,
              values: JSON.stringify({ [metric.year]: numericValue }),
              file_id: fileId,
            });
          }
        }
      });

      // Ensure all categories are represented
      if (ocrFinancialData?.categories) {
        ocrFinancialData.categories.forEach((cat) => {
          if (!categoryData[cat.id]) {
            categoryData[cat.id] = [];
          }
        });
      }

      const payload = {
        data: categoryData,
        deletedItems: JSON.stringify(deletedItems),
        fileId,
      };

      await api.post(`/ocr/hotel/${selectedHotelId}/save-financials`, payload);
      setSuccess('Financial data saved successfully!');
      setDeletedRows(new Set());
      setActiveStep((prevStep) => prevStep + 1);
    } catch (error: any) {
      console.error('Error saving financial data:', error);
      setError(error?.response?.data?.message || error?.message || 'Failed to save financial data');
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    activeStep,
    hotelInfo,
    existingHotels,
    selectedHotelId,
    isNewHotel,
    metrics,
    aiInsights,
    loading,
    error,
    saveStatus,
    dealData,
    showBackConfirm,
    selectedCategory,
    newItemName,
    newItemYear,
    newItemAmount,
    success,
    ocrFinancialData,
    hotelAiAnalysis,
    showLoginDialog,
    editedData,
    deletedRows,

    // Setters
    setHotelInfo,
    setMetrics,
    setError,
    setSuccess,
    setDealData,
    setSelectedCategory,
    setNewItemName,
    setNewItemYear,
    setNewItemAmount,
    setShowLoginDialog,
    setEditedData,
    setDeletedRows,
    setShowBackConfirm,

    // Handlers
    handleHotelSelect,
    handleAddNewItem,
    handleNext,
    handleBackClick,
    handleBackConfirm,
    handleSaveDeal,
    handleLoginSuccess,
    handleSaveFinancials
  };
};